import React, { useState } from 'react';
import { Plus, Ban } from 'lucide-react';
import FormModal from '../../../Modals/FormModal';
import ActionButton from '../../../Buttons/ActionButton';
import DesktopTableInlineActions from '../../../Tables/desktop/DesktopTableInlineActions';

import { useInventoryAdjustment } from '../../../../hooks/inventory/useInventoryAdjustment';
import { buildAdjustmentProductColumns } from './columns/adjustmentProductColumns';
import ProductStockPickerTable from '../../shared/ProductStockPickerTable';

import InventoryAdjustmentHeader from '../headers/InventoryAdjustmentHeader';
import InventoryAdjustmentStatusBar from '../statusBar/AdjustmentStatusBar';
import { useInventoryAdjustmentDetail } from '../../../../hooks/inventory/adjustment/useInventoryAdjustmentDetail';
import useConfirmModal from '../../../../hooks/useConfirmModal';
import { ProductStockLookupDTO } from '../../../../types/inventory/stock/ProductStockLookupTypes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const CreateInventoryAdjustmentModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const {
    form,
    updateField,
    clearForm,
    createAdjustment,
    finalize,
    updateHeader,
    cancel,
    isSubmitting,
  } = useInventoryAdjustment();

  const { addDetail, updateDetail, deleteDetailById } = useInventoryAdjustmentDetail();
  const confirm = useConfirmModal();
  const [showProductPicker, setShowProductPicker] = useState(false);
  const isEditable = form.status === 'Creado';
  const hasAdjustment = !!form.id;

  /* ───────────────────────────────
   * HANDLERS
   * ─────────────────────────────── */

  const handleStartAdjustment = async () => {
    await createAdjustment({
      branchId: form.branchId,
      reason: form.reason,
    });
  };

  const handleFinalizeAdjustment = async () => {
    if (!form.id) return;
    await finalize(form.id);
  };

  const handleAddProduct = () => {
    if (!hasAdjustment || !isEditable) return;
    setShowProductPicker(true);
  };

  // 🛑 El selector se queda ABIERTO tras elegir un producto — así se pueden
  // agregar varios seguidos sin tener que reabrirlo (y volver a pedir la
  // lista) cada vez. El producto ya agregado desaparece solo de la lista
  // porque se excluye por excludeProductIds.
  const handlePickProduct = async (product: ProductStockLookupDTO) => {
    await addDetail({
      productId: product.productId,
      adjustmentType: 'INCREASE',
      quantity: 1,
    });
  };

  const handleDetailChange = async (
    detailId: number,
    adjustmentType: 'INCREASE' | 'DECREASE',
    quantity: number
  ) => {
    await updateDetail(detailId, { adjustmentType, quantity });
  };

  const handleUpdateHeader = async () => {
    if (!hasAdjustment || !isEditable) return;

    await updateHeader(form.id!, {
      branchId: form.branchId,
      reason: form.reason,
    });
  };

  const handleClose = () => {
    clearForm();
    onClose();
  };

  // 🛑 "Cancelar" cuando ya existe un ajuste en estado "Creado" debe ANULARLO
  // de verdad (llamar al backend), no solo cerrar el modal — de lo contrario
  // el ajuste queda huérfano en estado "Creado" en la base de datos.
  const handleCancelClick = () => {
    if (!hasAdjustment || !isEditable) {
      handleClose();
      return;
    }

    confirm({
      title: '¿Anular este ajuste?',
      message:
        'Se cancelará el ajuste y todos los productos que agregaste. Esta acción no se puede deshacer.',
      icon: 'warning',
      confirmText: 'Anular ajuste',
      cancelText: 'Volver',
      onConfirm: async () => {
        await cancel(form.id!);
        handleClose();
      },
    });
  };

  const handleDeleteProduct = async (row: any) => {
    if (!isEditable) return;

    await deleteDetailById(row.id);
  };

  /* ───────────────────────────────
   * TABLE ACTIONS
   * ─────────────────────────────── */

  const productActions = isEditable
    ? (row: any) => [
        {
          label: 'Eliminar',
          icon: 'trash',
          variant: 'danger',
          onClick: () => handleDeleteProduct(row),
        },
      ]
    : undefined;

  const productColumns = buildAdjustmentProductColumns(handleDetailChange);

  /* ───────────────────────────────
   * RENDER
   * ─────────────────────────────── */

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Ajuste de Inventario"
        maxWidth="max-w-6xl"
        zIndex="normal"
      >
        {/* ─────────── HEADER ─────────── */}
        <InventoryAdjustmentHeader
          branchId={form.branchId}
          reason={form.reason}
          onChangeBranch={(value) => updateField('branchId', value)}
          onChangeReason={(value) => updateField('reason', value)}
          onUpdateHeader={handleUpdateHeader}
        />

        {/* STATUS BAR */}
        {hasAdjustment && (
          <InventoryAdjustmentStatusBar
            status={form.status}
            adjustedDate={form.adjustedDate}
            adjustedTime={form.adjustedTime}
          />
        )}

        {/* ─────────── DETAILS ─────────── */}
        <div className="mb-6 px-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold">Detalle de Productos</h3>

            {!showProductPicker && (
              <ActionButton
                label="Agregar Producto"
                icon={<Plus className="h-4 w-4" />}
                size="sm"
                fullWidth={false}
                disabled={!hasAdjustment || !isEditable}
                onClick={handleAddProduct}
              />
            )}
          </div>

          {/* ---------------------------- */}
          {/* Selector paginado de producto (sin modal) */}
          {/* ---------------------------- */}
          {showProductPicker && (
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold">Elegir producto</h4>
                <ActionButton
                  label="Cerrar"
                  variant="secondary"
                  size="sm"
                  fullWidth={false}
                  onClick={() => setShowProductPicker(false)}
                />
              </div>
              <ProductStockPickerTable
                branchId={form.branchId}
                onSelect={handlePickProduct}
                excludeProductIds={form.details.map((d: any) => d.productId)}
              />
            </div>
          )}

          <DesktopTableInlineActions
            columns={productColumns}
            data={form.details}
            actions={productActions}
            noDataMessage="No hay productos agregados."
          />
        </div>

        {/* ─────────── ACTIONS ─────────── */}
        <div className="flex flex-wrap justify-end gap-3 px-4">
          <ActionButton
            label={hasAdjustment && isEditable ? 'Anular Ajuste' : 'Cancelar'}
            icon={<Ban className="h-4 w-4" />}
            variant="danger"
            fullWidth={false}
            disabled={isSubmitting}
            onClick={handleCancelClick}
          />

          {/* INICIAR AJUSTE */}
          {!hasAdjustment && (
            <ActionButton
              label="Iniciar Ajuste"
              fullWidth={false}
              loading={isSubmitting}
              loadingLabel="Creando..."
              onClick={handleStartAdjustment}
            />
          )}

          {/* FINALIZAR AJUSTE */}
          {hasAdjustment && isEditable && (
            <ActionButton
              label="Finalizar Ajuste"
              variant="success"
              fullWidth={false}
              loading={isSubmitting}
              loadingLabel="Finalizando..."
              onClick={handleFinalizeAdjustment}
            />
          )}
        </div>
      </FormModal>
    </>
  );
};

export default CreateInventoryAdjustmentModal;
